/**
 * RouteSkeleton — Suspense fallback for lazy-loaded route boundaries.
 *
 * Phase 4 W4.T4 — initial implementation.
 * Phase 6 W4 T4.1 (R2 SR-1) — extended with per-route variants so the
 * fallback shape matches the destination page. Without variants every
 * lazy chunk-load shows the same 3-block placeholder which collapses
 * back into a tall heatmap or a 4-tab profile, producing a visible
 * layout shift on cold cache. Per-route variants keep CLS near 0.
 *
 * Variants:
 *   - `generic`    — 3-block placeholder (legacy, used by /projects,
 *                    /projects/:id, /clients, /dashboard).
 *   - `workload`   — filter-bar (12 chips) + 2-button tab strip +
 *                    7×8 heatmap grid.
 *   - `supervisor` — settings form (4 fields) + 2 dialog row outlines.
 *   - `profile`    — 4 tab placeholders + 5 form fields.
 *
 * Visual: each variant uses the shared `<Skeleton>` primitive (warm
 * surface tint + animate-pulse). Containers carry distinct
 * `data-testid` values so Playwright snapshots / unit tests can assert
 * on the rendered shape.
 *
 * R2 NIT-1 reminder: intra-route transitions (e.g. heatmap ↔ calendar
 * tab inside `/workload`) MUST remain synchronous. RouteSkeleton is
 * only the chunk-load fallback — do NOT wrap sub-pages in React.lazy.
 */
import * as React from 'react';

import { Skeleton } from '@/components/ui/skeleton';

export type SkeletonVariant = 'generic' | 'workload' | 'supervisor' | 'profile';

export interface RouteSkeletonProps {
  variant?: SkeletonVariant;
}

export function RouteSkeleton({
  variant = 'generic',
}: RouteSkeletonProps = {}): React.ReactElement {
  if (variant === 'workload') return <WorkloadRouteSkeleton />;
  if (variant === 'supervisor') return <SupervisorRouteSkeleton />;
  if (variant === 'profile') return <ProfileRouteSkeleton />;
  return <GenericRouteSkeleton />;
}

function GenericRouteSkeleton(): React.ReactElement {
  return (
    <div data-testid="route-skeleton" className="space-y-3 p-6">
      <Skeleton className="h-6 w-1/3" />
      <Skeleton className="h-32 w-full" />
      <Skeleton className="h-4 w-2/3" />
    </div>
  );
}

/**
 * `/workload` fallback — mirrors WorkloadPage shape:
 *   1. Filter bar (12 chip outlines).
 *   2. Tab strip (heatmap / calendar — 2 buttons).
 *   3. Heatmap 7×8 grid (56 cells).
 */
function WorkloadRouteSkeleton(): React.ReactElement {
  return (
    <div data-testid="route-skeleton-workload" className="space-y-3 p-6">
      {/* filter-bar placeholder: 12 chip outlines */}
      <div className="flex flex-wrap gap-2">
        {Array.from({ length: 12 }).map((_, i) => (
          <Skeleton key={`chip-${i}`} className="h-9 w-24" />
        ))}
      </div>
      {/* tab strip */}
      <div className="flex gap-2">
        <Skeleton className="h-10 w-20" />
        <Skeleton className="h-10 w-20" />
      </div>
      {/* heatmap 7×8 grid (56 cells) */}
      <div className="grid grid-cols-8 gap-1">
        {Array.from({ length: 56 }).map((_, i) => (
          <Skeleton key={`cell-${i}`} className="h-9" />
        ))}
      </div>
    </div>
  );
}

/**
 * `/supervisor` fallback — mirrors SupervisorSettingsPage shape:
 *   1. Form (4 fields, each label + input).
 *   2. Two dialog-row outlines (action rows).
 */
function SupervisorRouteSkeleton(): React.ReactElement {
  return (
    <div data-testid="route-skeleton-supervisor" className="space-y-6 p-6">
      <div className="space-y-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={`field-${i}`} className="space-y-2">
            <Skeleton className="h-4 w-32" />
            <Skeleton className="h-10 w-full max-w-md" />
          </div>
        ))}
      </div>
      <div className="space-y-2">
        {Array.from({ length: 2 }).map((_, i) => (
          <Skeleton key={`row-${i}`} className="h-14 w-full" />
        ))}
      </div>
    </div>
  );
}

/**
 * `/profile` fallback — mirrors ProfilePage shape:
 *   1. Tab strip (4 placeholders).
 *   2. Form (5 fields).
 */
function ProfileRouteSkeleton(): React.ReactElement {
  return (
    <div data-testid="route-skeleton-profile" className="space-y-6 p-6">
      <div className="flex gap-2">
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={`tab-${i}`} className="h-10 w-28" />
        ))}
      </div>
      <div className="space-y-4">
        {Array.from({ length: 5 }).map((_, i) => (
          <div key={`field-${i}`} className="space-y-2">
            <Skeleton className="h-4 w-24" />
            <Skeleton className="h-10 w-full max-w-md" />
          </div>
        ))}
      </div>
    </div>
  );
}
